/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef } from 'react';
import { useNotifications } from '../context/NotificationContext';
import { useAiContext } from '../providers/ai-provider';
import { AiSignal } from '../types/ai';

export const AiSignalSubscriber: React.FC = () => {
    const { addNotification } = useNotifications();
    const { signals, refreshData } = useAiContext();
    const seenSignalIds = useRef(new Set<string>());

    // On initial load, populate seen signals without notifying
    useEffect(() => {
        if (signals.length > 0 && seenSignalIds.current.size === 0) {
            signals.forEach(s => seenSignalIds.current.add(s.id));
        }
    }, [signals]);

    // Periodically refresh signals
    useEffect(() => {
        const interval = setInterval(() => {
            refreshData();
        }, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
    }, [refreshData]);

    // Process new signals and create notifications
    useEffect(() => {
        signals.forEach(signal => {
            if (!seenSignalIds.current.has(signal.id)) {
                seenSignalIds.current.add(signal.id);
                
                addNotification({
                    type: 'signal',
                    title: `AI Signal: ${signal.type} ${signal.token.symbol}`,
                    message: `Confidence: ${signal.confidence}%. ${signal.reasoning.slice(0, 50)}...`,
                    signal,
                    action: {
                        label: 'Analyze Signal',
                        onClick: () => {
                            // Dispatch a custom event that App.tsx will listen for
                            // This decouples the notification from direct modal control
                            window.dispatchEvent(new CustomEvent('open-ai-signal', { detail: signal }));
                        }
                    }
                });
            }
        });
    }, [signals, addNotification]);

    return null; // This is a non-visual component
};
